// Adapted from https://beui.dev/components/motion/pull-to-refresh.
// The upstream MIT notice is retained in ./LICENSE.txt.
import { useEffect } from "react";
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion, useTransform, type MotionValue } from "motion/react";
import { EASE_IN_OUT, EASE_OUT, SPRING_PANEL, SPRING_SWAP } from "./ease";

const HOLD_DISTANCE = 68;
const CHARACTER_LOOP = { duration: 0.9, ease: EASE_IN_OUT, repeat: Infinity } as const;

type IndicatorProps = {
  distance: number;
  threshold: number;
  refreshing: boolean;
  label: string;
};

function RefreshBuddy({ progress, ready, refreshing, reduce }: {
  progress: MotionValue<number>;
  ready: boolean;
  refreshing: boolean;
  reduce: boolean;
}) {
  const lift = useTransform(progress, [0, 1], [-7, 0]);
  const tilt = useTransform(progress, [0, 1], [-10, 0]);
  const stretch = useTransform(progress, [0, 0.55, 1], [0.68, 1.1, 0.92]);
  return <motion.span style={reduce ? undefined : { y: lift, rotate: tilt, scaleY: stretch }} className="block h-9 w-9 origin-bottom">
    <motion.svg aria-hidden="true" viewBox="0 0 36 36" className="h-full w-full overflow-visible"
      animate={reduce ? { y: 0, rotate: 0, scale: 1 } : refreshing
        ? { y: [0, -2, 0], rotate: [-3, 3, -3], scale: 1 }
        : { y: 0, rotate: 0, scale: ready ? 1.08 : 1 }}
      transition={refreshing && !reduce ? CHARACTER_LOOP : SPRING_SWAP}>
      <motion.g style={{ originX: "18px", originY: "18px", transformBox: "view-box" }}
        className={ready || refreshing ? "opacity-100" : "opacity-0"}
        animate={{ rotate: reduce ? 0 : refreshing ? [0, 360] : ready ? 0 : -35 }}
        transition={refreshing && !reduce ? CHARACTER_LOOP : SPRING_SWAP}>
        <path d="M18 2.5a15.5 15.5 0 0 1 12.7 6.6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-mut" />
        <circle cx="31.3" cy="10.2" r="2.2" className="fill-navy" />
      </motion.g>
      <rect x="7" y="7" width="22" height="22" rx="9" className="fill-navy" />
      <motion.g style={{ originX: "18px", originY: "16px", transformBox: "view-box" }}
        animate={{ scaleY: reduce ? 1 : refreshing ? [1, 1, 0.15, 1, 1] : ready ? 1.18 : 1 }}
        transition={refreshing && !reduce ? CHARACTER_LOOP : SPRING_SWAP}>
        <circle cx="14.2" cy="16" r="1.45" className="fill-white" />
        <circle cx="21.8" cy="16" r="1.45" className="fill-white" />
      </motion.g>
      <path d="M14.5 21h7" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={!ready && !refreshing ? 1 : 0} />
      <path d="M14 20.5c1 2.4 7 2.4 8 0" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity={ready && !refreshing ? 1 : 0} />
      <circle cx="18" cy="21" r="1.6" className="fill-white" opacity={refreshing ? 1 : 0} />
    </motion.svg>
  </motion.span>;
}

/** Reveals space in the document so caller-owned fixed sheets keep their viewport containing block. */
export function PullRefreshIndicator({ distance, threshold, refreshing, label }: IndicatorProps) {
  const reduce = Boolean(useReducedMotion());
  const height = useMotionValue(0);
  const progress = useTransform(height, [0, threshold], [0, 1]);
  const opacity = useTransform(height, [0, 10, threshold], [0, 0.45, 1]);
  const scale = useTransform(height, [0, threshold], [0.86, 1]);
  const state = refreshing ? "refreshing" : distance >= threshold ? "ready" : "pulling";
  const active = refreshing || distance > 0;

  useEffect(() => {
    const target = refreshing ? HOLD_DISTANCE : distance;
    if (reduce || distance > 0) {
      height.set(target);
      return;
    }
    const animation = animate(height, target, SPRING_PANEL);
    return () => animation.stop();
  }, [distance, refreshing, reduce, height]);

  return <motion.div style={{ height }} className="relative overflow-hidden" data-pull-refresh-indicator>
    <motion.div style={reduce ? { opacity } : { opacity, scale }}
      className="pointer-events-none absolute inset-x-0 top-0 flex h-[68px] flex-col items-center justify-center gap-0.5 text-navy">
      <RefreshBuddy progress={progress} ready={state === "ready"} refreshing={refreshing} reduce={reduce} />
      <span role="status" aria-live="polite" aria-atomic="true" className="relative h-4 min-w-24 text-center text-[11px] font-medium text-mut">
        <AnimatePresence initial={false} mode="wait">
          {active && <motion.span key={state}
            initial={reduce ? false : { opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -3 }}
            transition={{ duration: reduce ? 0 : 0.16, ease: EASE_OUT }}
            className="absolute inset-x-0 whitespace-nowrap">{label}</motion.span>}
        </AnimatePresence>
      </span>
    </motion.div>
  </motion.div>;
}
